import { useState, useEffect } from 'react'
import { Button, Tabs, TabItem, Input, Toast } from 'components/atoms/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { Dialog } from 'components/organisms/Modal/Dialog';
import { studentMypageApi, studentAuthApi } from 'services/api';

const STUDENT_SESSION_KEYS = [
    'student_token',
    'student_refresh_token',
    'student_user_id',
    'student_user_name',
    'student_display_name',
    'student_email',
    'student_login_id',
    'student_academy_code',
    'student_academy_name',
    'student_grade_label',
    'student_birth_date',
];

const clearStudentSession = () => {
    if (typeof window === 'undefined') return;
    STUDENT_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

interface StudentInfo {
    name: string | null;
    loginId: string;
    birthDate: string | null;
    parentEmail: string | null;
}

interface AcademyInfo {
    academyCode: string | null;
    academyName: string | null;
    className: string | null;
    teacherName: string | null;
}

interface EnrollmentInfo {
    enrollmentStatus: string;
    studentStatus: string;
    createdAt: string | null;
    enrolledAt: string | null;
}

interface MypageData {
    studentInfo: StudentInfo;
    academyInfo: AcademyInfo;
    enrollmentInfo: EnrollmentInfo;
}

// 학생 마이페이지
export default function StudentMyPageInfo() {
    const [showDialog, setShowDialog] = useState(false);
    const [mypageData, setMypageData] = useState<MypageData | null>(null);
    const [loading, setLoading] = useState(true);
    
    // 비밀번호 변경 관련 상태
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
    const [toast, setToast] = useState<{ message: string; status: 'success' | 'error' | 'info' } | null>(null);

    //탭 구성
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);
    const tabItems: TabItem[] = [
        { id: `${slugPrefix}/mypage/info`, label: '기본정보', onClick: () => navigate(`${slugPrefix}/mypage/info`) },
        { id: `${slugPrefix}/mypage/homework`, label: '숙제 채점표', onClick: () => navigate(`${slugPrefix}/mypage/homework`) },
        { id: `${slugPrefix}/mypage/exam`, label: '시험 성적표', onClick: () => navigate(`${slugPrefix}/mypage/exam`) },
        { id: `${slugPrefix}/mypage/trophy_badge`, label: '트로피 및 뱃지', onClick: () => navigate(`${slugPrefix}/mypage/trophy_badge`) },
      ];

    // 마이페이지 정보 로드
    useEffect(() => {
        loadMypageInfo();
    }, []);

    // Toast 자동 닫기
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => {
                setToast(null);
            }, 2500); // Toast duration(2000) + 여유 시간
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const loadMypageInfo = async () => {
        try {
            setLoading(true);
            const response = await studentMypageApi.getMyInfo();
            if (response.success) {
                setMypageData(response.data);
            } else {
                console.error('Failed to load mypage info:', response.message);
            }
        } catch (error) {
            console.error('Error loading mypage info:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await studentAuthApi.logout();
        } catch (error) {
            console.error('학생 로그아웃 API 실패:', error);
        } finally {
            clearStudentSession();
            const target = slugPrefix ? `${slugPrefix}/login` : '/login';
            navigate(target, { replace: true });
        }
    };

    // 날짜 포맷 함수 (년-월-일)
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch {
            return dateString;
        }
    };

    const getEnrollmentStatusLabel = (status?: string | null) => {
        if (!status) return '-';
        const statusMap: Record<string, string> = {
            ENROLLED: '수강중',
            SUSPENDED: '일시정지',
            WITHDRAWN: '탈퇴',
            PENDING: '대기중',
        };
        return statusMap[status] ?? status;
    };

    // 비밀번호 변경 핸들러
    const handlePasswordChange = async () => {
        // 입력 검증
        if (!currentPassword || !newPassword || !confirmPassword) {
            setToast({ message: '모든 필드를 입력해주세요.', status: 'error' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setToast({ message: '새 비밀번호가 일치하지 않습니다.', status: 'error' });
            return;
        }

        if (!/^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/.test(newPassword)) {
            setToast({ message: '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.', status: 'error' });
            return;
        }

        try {
            setPasswordChangeLoading(true);

            const response = await studentMypageApi.changePassword({
                currentPassword,
                newPassword,
                confirmPassword
            });

            console.log('비밀번호 변경 응답:', response);

            // API 응답 구조: { success: boolean, data: {...}, message: string }
            if (response.success === true) {
                // 성공 시 입력 필드 초기화
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowDialog(false);
                setToast({ message: '비밀번호가 성공적으로 변경되었습니다.', status: 'success' });
            } else {
                // 실패 시 에러 메시지 표시
                setToast({ message: response.message || '비밀번호 변경에 실패했습니다.', status: 'error' });
            }
        } catch (error: any) {
            console.error('비밀번호 변경 오류:', error);
            setToast({ message: error.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.', status: 'error' });
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    // 비밀번호 변경 모달 닫기
    const handleClosePasswordDialog = () => {
        setShowDialog(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    if (loading) {
        return (
            <main className='flex flex-1 pt-8 w-full bg-white md:my-10 md:justify-center card md:py-5 md:bg-transparent'>
                <div className='flex-1 w-full'>
                    <h1 className='px-5 pb-3 heading1'>마이페이지</h1>
                    <Tabs tabs={tabItems} activeId={pathname} />
                    <div className='px-5 py-3'>
                        <p className='text-center py-10'>로딩 중...</p>
                    </div>
                </div>
            </main>
        );
    }

  return (
    <main className='flex flex-1 pt-8 w-full bg-white md:my-10 md:justify-center card md:py-5 md:bg-transparent'>
        <div className='flex-1 w-full'>
            <div className='flex items-center justify-between px-5 pb-3'>
                <h1 className='heading1'>마이페이지</h1>
                <Button size='small' intent='secondary' onClick={handleLogout}>
                    로그아웃
                </Button>
            </div>
            <Tabs tabs={tabItems} activeId={pathname} />
            <div className='px-5 py-3'>
                <p className='text-sm text-gray-400 pb-'>정보 수정이 필요할 경우, 선생님께 문의하세요</p>
            <div className='flex flex-col gap-5 py-5 lg:grid lg:grid-cols-2 lg:grid-rows-2 lg:gap-4'>
                {/* 학생정보 */}
                <section className='row-span-2 lg:h-full'>
                    <p className='text-sm font-bold text-gray-500'>학생정보</p>
                        <ul className='mt-2 h-full gray_card'>
                            <li className='gray_card-info'>
                                <span className='gray_card-name'>이름</span>
                                    <p className='gray_card-data'>{mypageData?.studentInfo?.name || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>아이디</span>
                                    <p className='gray_card-data'>{mypageData?.studentInfo?.loginId || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>비밀번호</span>
                                <Button size='small' onClick={() => setShowDialog(true)}>비밀번호 변경하기</Button>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>생년월일</span>
                                    <p className='gray_card-data'>{formatDate(mypageData?.studentInfo?.birthDate || null)}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>학부모 이메일</span>
                                    <p className='gray_card-data'>{mypageData?.studentInfo?.parentEmail || '-'}</p>
                            </li>
                        </ul>
                </section>
                
                {/* 학원정보 */}
                <section className='lg:h-full'>
                    <p className='text-sm font-bold text-gray-500'>학원정보</p>
                        <ul className='mt-2 gray_card lg:h-calc(100% - 32px)'>
                            <li className='gray_card-info'>
                                <span className='gray_card-name'>학원코드</span>
                                    <p className='gray_card-data'>{mypageData?.academyInfo?.academyCode || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>학원명</span>
                                    <p className='gray_card-data'>{mypageData?.academyInfo?.academyName || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>클래스</span>
                                    <p className='gray_card-data'>{mypageData?.academyInfo?.className || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>담당교사</span>
                                    <p className='gray_card-data'>{mypageData?.academyInfo?.teacherName || '-'}</p>
                            </li>
                        </ul>
                </section>

                {/* 가입정보 */}
                <section className=''>
                    <p className='text-sm font-bold text-gray-500'>가입정보</p>
                        <ul className='mt-2 gray_card lg:h-full'>
                            <li className='gray_card-info'>
                                <span className='gray_card-name'>가입상태</span>
                                    <p className='gray_card-data'>{getEnrollmentStatusLabel(mypageData?.enrollmentInfo?.enrollmentStatus || null)}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>학생상태</span>
                                    <p className='gray_card-data'>{mypageData?.enrollmentInfo?.studentStatus || '-'}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>가입신청일</span>
                                    <p className='gray_card-data'>{formatDate(mypageData?.enrollmentInfo?.createdAt || null)}</p>
                            </li>

                            <li className='gray_card-info'>
                                <span className='gray_card-name'>가입 승인일</span>
                                    <p className='gray_card-data'>{formatDate(mypageData?.enrollmentInfo?.enrolledAt || null)}</p>
                            </li>
                        </ul>
                </section>
            </div>
            </div>
        </div>
              {showDialog && (
                <Dialog
                  okText="확인"
                  cancelText="취소"
                  onOk={handlePasswordChange}
                  onCancel={handleClosePasswordDialog}
                >
                    <div className='px-5 pt-6'>
                        <h2 className='pb-5 heading1'>비밀번호 변경</h2>
                        <div className='flex flex-col gap-5'>
                            <Input
                                placeholder='현재 비밀번호를 입력해주세요.'
                                label='현재 비밀번호'
                                type='password'
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                disabled={passwordChangeLoading}
                            />
                            <Input
                                placeholder='새 비밀번호를 입력해주세요.'
                                label='새 비밀번호'
                                type='password'
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={passwordChangeLoading}
                            />
                            <Input
                                placeholder='새 비밀번호를 다시 입력해주세요.'
                                label='새 비밀번호 확인'
                                type='password'
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={passwordChangeLoading}
                            />
                        </div>
                    </div>
                </Dialog>
              )}
              
              {/* Toast 메시지 */}
              {toast && (
                <Toast
                  message={toast.message}
                  status={toast.status}
                  isVisible={!!toast}
                />
              )}
    </main>
    
  )
}